import API from '@/api';
import V3API from '@/api/v3';
import { groupPermissions } from '../../utils/licenseDetails';

const LIST_LICENSES = 'LIST_LICENSES';
const LIST_MOBILE_LICENSES = 'LIST_MOBILE_LICENSES';
const LIST_MULTI_LICENSES = 'LIST_MULTI_LICENSES';
const LIST_LICENSES_SUCCESS = 'LIST_LICENSES_SUCCESS';
const LIST_MOBILE_LICENSES_SUCCESS = 'LIST_MOBILE_LICENSES_SUCCESS';
const LIST_MULTI_LICENSES_SUCCESS = 'LIST_MULTI_LICENSES_SUCCESS';
const LIST_LICENSES_FAILED = 'LIST_LICENSES_FAILED';
const LIST_MOBILE_LICENSES_FAILED = 'LIST_MOBILE_LICENSES_FAILED';
const LIST_MULTI_LICENSES_FAILED = 'LIST_MULTI_LICENSES_FAILED';

const SHOW_LICENSE = 'SHOW_LICENSE';
const SHOW_LICENSE_SUCCESS = 'SHOW_LICENSE_SUCCESS';
const SHOW_LICENSE_FAILED = 'SHOW_LICENSE_FAILED';

const APEX_LICENSE_GET = 'APEX_LICENSE_GET';
const APEX_LICENSE_GET_SUCCESS = 'APEX_LICENSE_GET_SUCCESS';
const APEX_LICENSE_GET_FAILED = 'APEX_LICENSE_GET_FAILED';

const DELETE_LICENSE = 'DELETE_LICENSE';
const DELETE_LICENSE_SUCCESS = 'DELETE_LICENSE_SUCCESS';
const DELETE_MOBILE_LICENSE_SUCCESS = 'DELETE_MOBILE_LICENSE_SUCCESS';
const DELETE_LICENSE_FAILED = 'DELETE_LICENSE_FAILED';

const SHOW_SIMULATOR = 'SHOW_SIMULATOR';
const SHOW_SIMULATOR_SUCCESS = 'SHOW_SIMULATOR_SUCCESS';
const SHOW_SIMULATOR_FAILED = 'SHOW_SIMULATOR_FAILED';

const RESET_LICENSES = 'RESET_LICENSES';

const SUBMIT_KEY = 'SUBMIT_KEY';
const SUBMIT_KEY_SUCCESS = 'SUBMIT_KEY_SUCCESS';
const SUBMIT_KEY_FAILED = 'SUBMIT_KEY_FAILED';
const DISMISS_KEY_MESSAGE = 'DISMISS_KEY_MESSAGE';
const SUBMIT_ERROR_MESSAGE = 'SUBMIT_ERROR_MESSAGE';
const GET_KEY = 'GET_KEY';
const GET_KEY_SUCCESS = 'GET_KEY_SUCCESS';
const GET_KEY_FAILED = 'GET_KEY_FAILED';
const RESET_KEY = 'RESET_KEY';

// Enabled the module to be namespaced
const namespaced = true;

// License initial state
const state = {
  all: [],
  licenses: [],
  mobileLicense: [],
  multiSportLicenses: [],
  detailedLicense: {},
  apexLicense: {},
  loadingLicenses: false,
  loadingLicense: false,
  loadingApexLicense: false,
  loadingMobileLicense: false,
  loadingMultiSportLicense: false,
  simulators: [],
  loadingSimulator: false,
  unbound: false,
  loadingKey: false,
  productKey: {},
  productKeySuccess: false,
  productKeyError: false,
  errorMessage: '',
};

// License getters
const getters = {
  // licenseList returns a list of licenses
  licenseList: (state) => {
    return state.all.sort((x, y) => {
      return x.license?.available === y.license?.available ? 0 : x.license?.available ? 1 : -1;
    });
  },

  licenseListV3: (state) => {
    return state.all.sort((x, y) => {
      return x.license?.available === y.license?.available ? 0 : x.license?.available ? 1 : -1;
    });
  },

  mobileLicenseList: (state) => {
    return state.mobileLicense;
  },

  multiLicenseList: (state) => {
    return state.multiSportLicenses;
  },

  // detailedLicense returns a license with verbose details
  detailedLicense: (state) => {
    return state.detailedLicense;
  },

  // apexLicense returns an apex license with details
  apexLicense: (state) => {
    return state.apexLicense;
  },

  // loadingLicenses returns the state of loading the licenses
  loadingLicenses: (state) => {
    return state.loadingLicenses;
  },

  // loadingLicense returns the state of loading a license
  loadingLicense: (state) => {
    return state.loadingLicense;
  },

  // loadingApexLicense returns the state of loading the apex license
  loadingApexLicense: (state) => {
    return state.loadingApexLicense;
  },

  // loadingMobileLicense returns the state of loading the mobile license
  loadingMobileLicense: (state) => {
    return state.loadingMobileLicense;
  },

  // loadingMultiLicense returns the state of loading the mobile license
  loadingMultiSportLicense: (state) => {
    return state.loadingMultiSportLicense;
  },

  // simulators returns a list of simulators
  simulators: (state) => {
    return state.simulators;
  },

  // unbound returns true if a simulator was successfully deleted
  unbound: (state) => {
    return state.unbound;
  },

  loadingKey: (state) => {
    return state.loadingKey;
  },

  productKey: (state) => {
    return state.productKey;
  },

  productKeySuccess: (state) => {
    return state.productKeySuccess;
  },

  productKeyError: (state) => {
    return state.productKeyError;
  },

  errorMessage: (state) => {
    return state.errorMessage;
  },
};

// License actions
//* Helper function to process license data consistently
const processLicenseData = (originalLicense, simulatorDetailsResponse, apexLicenseResponse) => {
  //* Handle case where simulator details are missing
  if (!simulatorDetailsResponse?.data) {
    return { license: '', clientName: '' };
  }

  const { clientName } = simulatorDetailsResponse.data?.authentication || {};

  //* Build the license object with consistent structure
  const licenseData = {
    license: {
      ...simulatorDetailsResponse.data,
      name: apexLicenseResponse?.data?.name || simulatorDetailsResponse.data?.name,
    },
    clientName,
    ...(apexLicenseResponse?.data?.apex_binding && {
      apexBinding: apexLicenseResponse?.data?.apex_binding,
    }),
  };

  return licenseData;
};

const actions = {
  //* all fetches the players desktop licenses list from the API
  async all({ commit }, payload) {
    let licenses = {};

    commit(LIST_LICENSES);
    commit(LIST_MULTI_LICENSES);
    // commit(RESET_KEY);
    commit(RESET_LICENSES);

    //* get desktop license list
    try {
      licenses = await API.licenses.list();

      if (licenses.status !== 200 || !licenses.data) {
        commit(LIST_LICENSES_FAILED);
        commit(LIST_MULTI_LICENSES_FAILED);
        return licenses;
      }

      //* get simulator info for all of those licenses
      licenses = await Promise.all(
        licenses.data.map(async (license) => {
          try {
            //* Make both API calls in parallel for better performance
            const [simulatorDetailsResponse, apexLicenseResponse] = await Promise.all([
              API.licenses.show(license.id),
              V3API.licenses.licenseGet({ id: license.id }),
            ]);

            return processLicenseData(license, simulatorDetailsResponse, apexLicenseResponse);
          } catch (error) {
            console.error(`Error fetching license details for ${license.id}:`, error);
            //* Return a minimal license object to prevent breaking the entire list
            return {
              license: { id: license.id, name: 'Error loading license' },
              clientName: '',
              error: true,
            };
          }
        })
      );

      //* Filter out multi-sport licenses
      let multiSportLicenses = [];
      licenses = licenses?.filter((license) => {
        /* eslint-disable camelcase */
        const { license_key } = license?.license ?? {};
        if (!license_key) {
          return;
        }

        if (license_key && license_key.startsWith('multi')) {
          multiSportLicenses.push(license);
        }
        return !license_key.startsWith('multi');
      });

      if (multiSportLicenses.length) {
        try {
          multiSportLicenses = multiSportLicenses.map((license) => {
            if (!license) {
              return {};
            }
            if (!license?.license?.permissions) {
              license.license.permissions = [];
            }

            //* Util to return all grouped permissions
            const grouped = groupPermissions(license?.license);

            return {
              ...license,
              courses: grouped.courses ?? [],
              apexCourses: grouped.apexCourses ?? [],
              connectCourses: grouped.connectCourses ?? [],
              trackingSystems: grouped.trackingSystems ?? [],
              permissions: grouped.permissions ?? [],
              sortOrder: 3,
            };
          });
          commit(LIST_MULTI_LICENSES_SUCCESS, multiSportLicenses);
        } catch (err) {
          return new Promise((resolve, reject) => {
            commit(LIST_MULTI_LICENSES_FAILED);
            reject(err);
          });
        }
      } else {
        commit(LIST_MULTI_LICENSES_FAILED);
      }

      //* Group permissions
      licenses = licenses.map((simulator) => {
        if (!simulator) {
          return {};
        }
        if (!simulator?.license?.permissions) {
          simulator.license.permissions = [];
        }

        //* Util to return all grouped permissions
        const grouped = groupPermissions(simulator?.license);

        return {
          ...simulator,
          courses: grouped.courses ?? [],
          apexCourses: grouped.apexCourses ?? [],
          connectCourses: grouped.connectCourses ?? [],
          trackingSystems: grouped.trackingSystems ?? [],
          permissions: grouped.permissions ?? [],
          sortOrder: 2,
        };
      });
    } catch (err) {
      return new Promise((resolve, reject) => {
        commit(LIST_LICENSES_FAILED);
        commit(LIST_MULTI_LICENSES_FAILED);
        reject(err);
      });
    }

    return new Promise((resolve, reject) => {
      commit(LIST_LICENSES_SUCCESS, licenses);

      // Return GET data within promise
      resolve(licenses);
    });
  },

  //* listAll v3 fetches ALL the users licenses from the API with product and binding info
  async listAll({ commit }, payload) {
    let licenses = {};

    commit(LIST_LICENSES);
    commit(LIST_MULTI_LICENSES);
    commit(RESET_LICENSES);

    //* get license list
    try {
      licenses = await V3API.licenses.licenseList();

      if (licenses.status !== 200 || !licenses.data) {
        commit(LIST_LICENSES_FAILED);
        commit(LIST_MULTI_LICENSES_FAILED);
        return licenses;
      }

      // //* Filter out multi-sport licenses
      // let multiSportLicenses = [];
      // licenses = licenses?.data?.filter((license) => {
      //   const { license_key } = license ?? {};
      //   if (!license_key) {
      //     return;
      //   }

      //   if (license_key && license_key.startsWith('multi')) {
      //     multiSportLicenses.push(license);
      //   }
      //   return !license_key.startsWith('multi');
      // });

      // if (multiSportLicenses.length) {
      //   try {
      //     multiSportLicenses = multiSportLicenses.map((license) => {
      //       if (!license) {
      //         return {};
      //       }
      //       if (!license?.permissions) {
      //         license.permissions = [];
      //       }

      //       //* Util to return all grouped permissions
      //       const grouped = groupPermissions(license);

      //       return {
      //         ...license,
      //         available: license?.connect_binding === null,
      //         availableConnect: license?.connect_binding === null,
      //         courses: grouped.courses ?? [],
      //         trackingSystems: grouped.trackingSystems ?? [],
      //         permissions: grouped.permissions ?? [],
      //         sortOrder: 3,
      //       };
      //     });
      //     commit(LIST_MULTI_LICENSES_SUCCESS, multiSportLicenses);
      //   } catch (err) {
      //     return new Promise((resolve, reject) => {
      //       commit(LIST_MULTI_LICENSES_FAILED);
      //       reject(err);
      //     });
      //   }
      // } else {
      //   commit(LIST_MULTI_LICENSES_FAILED);
      // }

      //* Group permissions
      licenses = licenses?.data
        ?.map((simulator) => {
          if (!simulator) {
            return {};
          }
          if (!simulator?.permissions) {
            simulator.permissions = [];
          }

          //* Util to return all grouped permissions
          const grouped = groupPermissions(simulator);

          return {
            ...simulator,
            // apexBinding: simulator?.apex_binding, // Do I need this?
            available: simulator?.connect_binding === null,
            availableConnect: simulator?.connect_binding === null,
            availableApex: simulator?.apex_binding === null,
            courses: grouped.courses ?? [],
            apexCourses: grouped.apexCourses ?? [],
            connectCourses: grouped.connectCourses ?? [],
            trackingSystems: grouped.trackingSystems ?? [],
            permissions: grouped.permissions ?? [],
            sortOrder: simulator?.license_key.startsWith('multi')
              ? 3
              : simulator.license_type === 'mobile'
              ? 1
              : 2,
          };
        })
        ?.sort((a, b) => {
          return a.sortOrder > b.sortOrder ? 1 : -1;
        });
    } catch (err) {
      return new Promise((resolve, reject) => {
        commit(LIST_LICENSES_FAILED);
        commit(LIST_MULTI_LICENSES_FAILED);
        reject(err);
      });
    }

    return new Promise((resolve, reject) => {
      commit(LIST_LICENSES_SUCCESS, licenses);

      // Return GET data within promise
      resolve(licenses);
    });
  },

  //* mobile fetches all mobile licenses from the API
  async mobile({ commit }) {
    let mobileLicenses = {};

    commit(LIST_MOBILE_LICENSES);
    // commit(RESET_KEY);
    commit(RESET_LICENSES);

    // * get mobile license list
    try {
      mobileLicenses = await API.licenses.listMobile();

      // * get and group info for the license
      // * if mobile license returns anything but 200 status, return
      if (mobileLicenses.status !== 200 || mobileLicenses.data === null || !mobileLicenses.data) {
        // commit(LIST_MOBILE_LICENSES_FAILED);
        // return mobileLicenses;
        mobileLicenses.data = [];
      }

      //* get license info from all licenses
      mobileLicenses = await Promise.all(
        mobileLicenses.data.map(async (mobileLicense) => {
          const licenseDetailsResponse = await API.licenses.showMobile(mobileLicense.id);
          const apexLicenseResponse = await V3API.licenses.licenseGet({ id: mobileLicense.id });
          if (
            mobileLicense?.clientTrackingID !== undefined &&
            mobileLicense?.clientTrackingID !== null
          ) {
            if (licenseDetailsResponse && licenseDetailsResponse.data !== undefined) {
              const { clientName } = licenseDetailsResponse.data?.authentication || {};

              return {
                license: licenseDetailsResponse.data,
                clientName,
                ...(apexLicenseResponse?.data?.apex_binding && {
                  apexBinding: apexLicenseResponse?.data?.apex_binding,
                }),
                // ...(apexLicenseResponse?.data?.bindings?.length && {
                //   apexBindings: apexLicenseResponse?.data?.bindings,
                // }),
              };
            }
            return { license: '', clientName: '' };
          }

          return {
            license: licenseDetailsResponse.data,
            ...(apexLicenseResponse?.data?.apex_binding && {
              apexBinding: apexLicenseResponse?.data?.apex_binding,
            }),
            // ...(apexLicenseResponse?.data?.bindings?.length && {
            //   apexBindings: apexLicenseResponse?.data?.bindings,
            // }),
          };
        })
      );

      //* Group mobile permissions
      mobileLicenses = mobileLicenses.map((mobileLicense) => {
        if (!mobileLicense) {
          return {};
        }
        if (!mobileLicense?.license?.permissions) {
          mobileLicense.license.permissions = [];
        }

        //* Util to return all grouped permissions
        const grouped = groupPermissions(mobileLicense?.license);

        return {
          ...mobileLicense,
          courses: grouped.courses ?? [],
          apexCourses: grouped.apexCourses ?? [],
          connectCourses: grouped.connectCourses ?? [],
          trackingSystems: grouped.trackingSystems ?? [],
          permissions: grouped.permissions ?? [],
          sortOrder: 1,
        };
      });
    } catch (err) {
      return new Promise((resolve, reject) => {
        commit(LIST_MOBILE_LICENSES_FAILED);
        reject(err);
      });
    }

    return new Promise((resolve, reject) => {
      commit(LIST_MOBILE_LICENSES_SUCCESS, mobileLicenses);
      // Return GET data within promise
      resolve(mobileLicenses);
    });
  },

  //* show fetches details for a desktop license from the API
  async show({ commit }, { licenseID, isAdmin = false }) {
    let license;

    commit(SHOW_LICENSE);

    try {
      const licenseGetEndpoint = isAdmin ? 'adminLicenseGet' : 'licenseGet';

      //* Parallel API calls for better performance
      const [licenseResponse, apexLicenseResponse] = await Promise.all([
        API.licenses.show(licenseID),
        V3API.licenses[licenseGetEndpoint]({ id: licenseID }),
      ]);

      license = licenseResponse;

      //* Check for multi-sport
      const { license_key } = license?.data ?? {};
      if (license_key?.startsWith('multi')) {
        license.data.type = 'multi-sport';
      } else {
        license.data.type = 'desktop';
      }

      //* Group permissions
      if (!license.data) return {};
      if (!license?.data?.permissions) {
        license.data.permissions = [];
      }

      //* Merge V3 permissions in, keyed by game + key; V3 wins on duplicates
      const apexPermissions =
        apexLicenseResponse?.status < 400 ? apexLicenseResponse?.data?.permissions ?? [] : [];
      license.data.permissions = Object.values(
        [...license.data.permissions, ...apexPermissions].reduce((acc, perm) => {
          acc[`${perm.game}:${perm.key}`] = perm;
          return acc;
        }, {})
      );

      //* Util to return all grouped permissions
      const grouped = groupPermissions(license?.data);

      license.data = {
        license: {
          ...license?.data,
          name: apexLicenseResponse?.data?.name || license?.data?.name,
        },
        type: license?.data?.type,
        courses: grouped.courses ?? [],
        apexCourses: grouped.apexCourses ?? [],
        connectCourses: grouped.connectCourses ?? [],
        trackingSystems: grouped.trackingSystems ?? [],
        permissions: grouped.permissions ?? [],
        clientName: license?.data?.authentication?.clientName,
        ...(apexLicenseResponse?.data?.apex_binding && {
          apexBinding: apexLicenseResponse?.data?.apex_binding,
        }),
        // ...(apexLicenseResponse?.data?.bindings?.length && {
        //   apexBindings: apexLicenseResponse?.data?.bindings,
        // }),
      };
    } catch (err) {
      license = err;
    }

    return new Promise((resolve, reject) => {
      if (license.status >= 400) {
        commit(SHOW_LICENSE_FAILED);
        reject(license);
      } else {
        commit(SHOW_LICENSE_SUCCESS, license.data);

        // Return GET data within promise
        resolve(license.data);
      }
    });
  },

  //* showMobile fetches details for a mobile license from the API
  async showMobile({ commit }, { licenseID, isAdmin = false }) {
    let mobileLicense;

    commit(SHOW_LICENSE);

    try {
      mobileLicense = await API.licenses.showMobile(licenseID);
      const licenseGetEndpoint = isAdmin ? 'adminLicenseGet' : 'licenseGet';
      const apexLicenseResponse = await V3API.licenses[licenseGetEndpoint]({ id: licenseID });

      //* Group permissions
      if (!mobileLicense.data) return {};
      if (!mobileLicense?.data?.permissions) {
        mobileLicense.data.permissions = [];
      }

      //* Util to return all grouped permissions
      const grouped = groupPermissions(mobileLicense?.data);

      mobileLicense.data = {
        license: mobileLicense?.data,
        clientName: mobileLicense?.data?.authentication?.clientName,
        type: 'mobile',
        courses: grouped.courses ?? [],
        apexCourses: grouped.apexCourses ?? [],
        connectCourses: grouped.connectCourses ?? [],
        trackingSystems: grouped.trackingSystems ?? [],
        permissions: grouped.permissions ?? [],
        ...(apexLicenseResponse?.data?.apex_binding && {
          apexBinding: apexLicenseResponse?.data?.apex_binding,
        }),
        // ...(apexLicenseResponse?.data?.bindings?.length && {
        //   apexBindings: apexLicenseResponse?.data?.bindings,
        // }),
      };
    } catch (err) {
      mobileLicense = err;
    }

    return new Promise((resolve, reject) => {
      if (mobileLicense.status >= 400) {
        commit(SHOW_LICENSE_FAILED);
        reject(mobileLicense);
      } else {
        commit(SHOW_LICENSE_SUCCESS, mobileLicense.data);

        // Return GET data within promise
        resolve(mobileLicense.data);
      }
    });
  },

  /**
   * delete desktop simulator/license
   */
  async delete({ commit }, licenseID) {
    let response;

    commit(DELETE_LICENSE);

    try {
      response = await API.licenses.destroy(licenseID);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(DELETE_LICENSE_FAILED);
        reject(response);
      } else {
        commit(DELETE_LICENSE_SUCCESS, licenseID);

        // TODO override list cache

        // Return GET data within promise
        resolve(response.data);
      }
    });
  },

  /**
   * delete mobile simulator/license
   */
  async deleteMobile({ commit }, licenseID) {
    let response;

    commit(DELETE_LICENSE);

    try {
      response = await API.licenses.destroyMobile(licenseID);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(DELETE_LICENSE_FAILED);
        reject(response);
      } else {
        commit(DELETE_MOBILE_LICENSE_SUCCESS, licenseID);

        // Return GET data within promise
        resolve(response.data);
      }
    });
  },

  /**
   * show fetches details for a simulator from the API
   */
  async showSim({ commit }, simulatorID) {
    let response;

    commit(SHOW_SIMULATOR);

    try {
      response = await API.licenses.showSim(simulatorID);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(SHOW_SIMULATOR_FAILED);
        reject(response);
      } else {
        commit(SHOW_SIMULATOR_SUCCESS, response.data);

        // Return GET data within promise
        resolve(response.data);
      }
    });
  },

  async resetLicenses({ commit }) {
    commit(RESET_LICENSES);
  },

  //* submitKey sends key and returns license code from the API
  async submitKey({ commit, dispatch, rootGetters }, payload) {
    let response;
    const { licenseID = '', isPermission = false } = payload;
    const limitedUser = rootGetters['users/limitedUser'];

    if (limitedUser) {
      return new Promise((resolve, reject) => {
        commit(SUBMIT_KEY_FAILED);
        response = {
          status: 428,
          type: 'userError',
          msg: 'Error P1006B: Your account is limited. Please login using your email and password or contact TruGolf at Toll Free: 866-711-GOLF.',
        };

        reject(response);
      });
    }

    if (!licenseID && !isPermission) {
      return new Promise((resolve, reject) => {
        commit(SUBMIT_KEY_FAILED);
        response = {
          status: 428,
          type: 'licenseError',
          msg: 'Error P1006: No license ID found to redeem. Please contact TruGolf at Toll Free: 866-711-GOLF.',
        };

        reject(response);
      });
    }

    commit(SUBMIT_KEY);

    try {
      //* this is the API call with the productKey payload
      response = await API.licenses.submitKey(payload);
      // console.log('response:', response);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response === undefined || response.status >= 400) {
        const noMessage = 'error redeeming code. please check information entered.';
        try {
          const { reason } = response?.data;
          const inUse = 'product key in use';
          const invalidKey = 'invalid key';
          const {
            data: { error: { code, msg } = {} },
          } = response;

          if (reason) {
            const [initialMessage] = reason?.split(',');
            const [message] = initialMessage?.split('.');

            if (reason.indexOf(inUse) >= 0) {
              commit(SUBMIT_KEY_FAILED);
              // Also works but can break easily if error string changes slightly
              //* message = Reason.slice(0, 26)

              // message = Reason.split(',')[0].split('.')[0];
              commit(SUBMIT_ERROR_MESSAGE, message);
            } else if (reason.indexOf(invalidKey) >= 0) {
              // Also works but can break easily if error string changes slightly
              //* message = Reason.slice(0, 19)

              commit(SUBMIT_ERROR_MESSAGE, message);
            } else {
              commit(SUBMIT_ERROR_MESSAGE, noMessage);
            }
            reject(response);
            return;
          }

          if (code && code === 'GEN') {
            commit(SUBMIT_KEY_FAILED);
            commit(SUBMIT_ERROR_MESSAGE, `Error ${code}: ${msg}`);
            reject(response);
            return;
          }

          commit(SUBMIT_KEY_FAILED);
          commit(SUBMIT_ERROR_MESSAGE, noMessage);

          reject(response);
          return;
        } catch (error) {
          console.error('error:', error);
          commit(SUBMIT_KEY_FAILED);
          commit(SUBMIT_ERROR_MESSAGE, noMessage);
        }
        reject(response);
      } else {
        if (response.data?.desktopLicenseID) {
          dispatch('all');
        } else if (response.data?.mobileLicenseID) {
          dispatch('mobile');
        }
        commit(SUBMIT_KEY_SUCCESS, response.data);

        // * Return data with promise
        resolve(response.data);
      }
    });
    // .catch((err) => {
    //   console.error(err);
    //   try {
    //     const {
    //       data: { error: { code, msg } = {} },
    //     } = err;
    //     commit(SUBMIT_KEY_FAILED);
    //     if (!msg) {
    //       commit(SUBMIT_ERROR_MESSAGE, `Error redeeming code`);
    //       return;
    //     }
    //     commit(SUBMIT_ERROR_MESSAGE, `Error ${code}: ${msg}`);
    //     return;
    //   } catch (error) {
    //     commit(SUBMIT_KEY_FAILED);
    //     commit(SUBMIT_ERROR_MESSAGE, `Error redeeming code`);
    //   }
    // });
  },

  // * getProductKey gets the product key info from the API
  async getProductKey({ commit, dispatch }, payload) {
    let response;
    let product;
    let products;

    commit(GET_KEY);

    try {
      //* this is the API call with the productKey payload
      response = await API.licenses.getProductKey(payload);

      const { skucode, skucodes } = response.data;

      if (skucode) {
        product = await dispatch('shop/showSku', skucode, { root: true });
      }

      if (skucodes?.length) {
        products = await Promise.allSettled(
          skucodes?.map(async (sku) => {
            const productResponse = await dispatch('shop/showSku', sku, { root: true });
            return productResponse;
          })
        );
        products = products?.map((item) => {
          return item.status === 'fulfilled' ? item.value : null;
        });
      }

      response.data = { ...response.data, product, products };
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response === undefined || response.status >= 400) {
        if (response.status >= 400) {
          commit(GET_KEY_FAILED);
          reject(response);
        } else {
          commit(GET_KEY_SUCCESS, response.data);

          // Return GET data within promise
          resolve(response.data);
        }

        commit(GET_KEY_FAILED);
        reject(response);
      } else {
        commit(GET_KEY_SUCCESS, response.data);

        // * Return data with promise
        resolve(response.data);
      }
    }).catch((e) => {
      console.error(e);
      commit(GET_KEY_FAILED);
    });
  },

  dismissKeyMessage({ commit }, payload) {
    commit(DISMISS_KEY_MESSAGE, payload);
    commit(RESET_KEY);
  },

  async apexLicenseGet({ commit }, payload) {
    let response;

    commit(APEX_LICENSE_GET);

    try {
      response = await V3API.licenses.licenseGet(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(APEX_LICENSE_GET_FAILED);
        reject(response);
      } else {
        commit(APEX_LICENSE_GET_SUCCESS);
        resolve(response);
      }
    });
  },

  async apexBindingDisable({ commit }, payload) {
    let response;

    commit(APEX_LICENSE_GET);

    try {
      response = await V3API.licenses.licenseBindingDisable(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(APEX_LICENSE_GET_FAILED);
        reject(response);
      } else {
        commit(APEX_LICENSE_GET_SUCCESS);
        resolve(response);
      }
    });
  },

  async licenseNameUpdate({ commit }, payload) {
    let response;

    commit(APEX_LICENSE_GET);

    try {
      response = await V3API.licenses.licenseNameUpdate(payload);
    } catch (err) {
      response = err;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400 || response.status === 0) {
        commit(APEX_LICENSE_GET_FAILED);
        reject(response);
      } else {
        commit(APEX_LICENSE_GET_SUCCESS);
        resolve(response);
      }
    });
  },
};

// License mutations
const mutations = {
  [LIST_LICENSES](state) {
    state.loadingLicenses = true;
  },

  [LIST_MOBILE_LICENSES](state) {
    state.loadingMobileLicense = true;
  },

  [LIST_MULTI_LICENSES](state) {
    state.loadingMultiSportLicense = true;
  },

  [LIST_LICENSES_SUCCESS](state, licenses) {
    state.all = licenses;
    state.loadingLicenses = false;
  },

  [LIST_MOBILE_LICENSES_SUCCESS](state, license) {
    state.mobileLicense = license;
    state.loadingMobileLicense = false;
  },

  [LIST_MULTI_LICENSES_SUCCESS](state, license) {
    state.multiSportLicenses = license;
    state.loadingMultiSportLicense = false;
  },

  [LIST_LICENSES_FAILED](state) {
    state.loadingLicenses = false;
  },

  [LIST_MOBILE_LICENSES_FAILED](state) {
    state.loadingMobileLicense = false;
  },

  [LIST_MULTI_LICENSES_FAILED](state) {
    state.loadingMultiSportLicense = false;
  },

  [SHOW_LICENSE](state) {
    state.loadingLicense = true;
  },

  [SHOW_LICENSE_SUCCESS](state, license) {
    state.detailedLicense = license;
    state.loadingLicense = false;
  },

  [SHOW_LICENSE_FAILED](state) {
    state.loadingLicense = false;
  },

  [DELETE_LICENSE](state) {
    state.unbinding = true;
    state.unbound = false;
  },

  [DELETE_LICENSE_SUCCESS](state, licenseID) {
    state.licenses = state.licenses.filter((license) => {
      return license.id !== licenseID;
    });

    state.unbinding = false;
    state.unbound = true;
  },

  [DELETE_MOBILE_LICENSE_SUCCESS](state, licenseID) {
    state.mobileLicense = state.mobileLicense.filter((license) => {
      return license.id !== licenseID;
    });

    state.unbinding = false;
    state.unbound = true;
  },

  [DELETE_LICENSE_FAILED](state) {
    state.unbinding = false;
    state.unbound = false;
  },

  [SHOW_SIMULATOR](state) {
    state.loadingSimulator = true;
  },

  [SHOW_SIMULATOR_SUCCESS](state, simulator) {
    state.simulators = [...state.simulators, simulator];
    state.loadingSimulator = false;
  },

  [SHOW_SIMULATOR_FAILED](state) {
    state.loadingSimulator = false;
  },

  [RESET_LICENSES](state) {
    state.all = [];
    state.multiSportLicenses = [];
    state.mobileLicense = [];
    state.detailedLicense = {};
    state.productKeySuccess = false;
    state.productKeyError = false;
    state.errorMessage = '';
  },

  [SUBMIT_KEY](state) {
    state.loadingKey = true;
  },

  [SUBMIT_KEY_SUCCESS](state, license) {
    state.loadingKey = false;
    state.productKeySuccess = true;
  },

  [SUBMIT_KEY_FAILED](state) {
    state.loadingKey = false;
    state.productKeyError = true;
  },

  [GET_KEY](state) {
    state.loadingKey = true;
  },

  [GET_KEY_SUCCESS](state, key) {
    state.loadingKey = false;
    state.productKey = key;
  },

  [GET_KEY_FAILED](state) {
    state.loadingKey = false;
    state.productKeyError = true;
  },

  [RESET_KEY](state) {
    state.loadingKey = false;
    state.productKey = {};
  },

  [DISMISS_KEY_MESSAGE](state, payload) {
    state.productKeySuccess = payload;
    state.productKeyError = payload;
    state.errorMessage = '';
  },

  [SUBMIT_ERROR_MESSAGE](state, payload) {
    state.errorMessage = payload;
  },

  [APEX_LICENSE_GET](state) {
    state.loadingApexLicense = true;
  },

  [APEX_LICENSE_GET_SUCCESS](state, license) {
    state.apexLicense = license;
    state.loadingApexLicense = false;
  },

  [APEX_LICENSE_GET_FAILED](state, license) {
    state.loadingApexLicense = false;
  },
};

export default {
  namespaced,
  state,
  actions,
  getters,
  mutations,
};
